<template>
  <Combobox as="div" :model-value="modelValue" :disabled="disabled" @update:model-value="onSelect">
    <ComboboxLabel class="block text-sm font-medium text-gray-900 dark:text-white">
      Time zone
    </ComboboxLabel>
    <div class="relative mt-1">
      <ComboboxInput
        :id="id"
        class="block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:focus:outline-indigo-500"
        :class="error ? 'outline-red-500 dark:outline-red-400' : ''"
        :display-value="(zone: unknown) => String(zone ?? '')"
        :aria-invalid="Boolean(error)"
        :aria-describedby="error ? `${id}-error` : undefined"
        placeholder="Search, e.g. Berlin"
        autocomplete="off"
        @change="onQuery"
      />
      <ComboboxButton
        class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden"
        aria-label="Show time zones"
      >
        <ChevronUpDownIcon class="size-5 text-gray-400" aria-hidden="true" />
      </ComboboxButton>
      <ComboboxOptions
        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 sm:text-sm dark:bg-gray-800 dark:outline-white/10"
      >
        <li v-if="matches.length === 0" class="px-3 py-2 text-gray-500 dark:text-gray-400">
          No time zone matches “{{ query }}”.
        </li>
        <ComboboxOption
          v-for="zone in matches"
          :key="zone"
          v-slot="{ active, selected }"
          :value="zone"
          as="template"
        >
          <li
            class="flex cursor-default items-center justify-between gap-3 px-3 py-2 select-none"
            :class="active ? 'bg-indigo-600 text-white' : 'text-gray-900 dark:text-white'"
          >
            <span class="truncate" :class="selected ? 'font-semibold' : ''">{{ zone }}</span>
            <span
              class="shrink-0 text-xs"
              :class="active ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400'"
              >{{ offsetLabel(zone) }}</span
            >
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
    <p v-if="error" :id="`${id}-error`" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>
  </Combobox>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import { browserTimeZone } from '@/lib/schedule';

const props = defineProps<{
  id: string;
  modelValue: string;
  error?: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>();

const MAX_MATCHES = 100;

function supportedZones(): string[] {
  const intl = Intl as { supportedValuesOf?: (key: string) => string[] };
  const zones = intl.supportedValuesOf?.('timeZone') ?? [];
  return zones.includes('UTC') ? zones : [...zones, 'UTC'];
}

const ALL_ZONES = supportedZones();
const query = ref('');

// The browser and the stored zone come first, and may be aliases missing from the canonical list.
const zones = computed(() => {
  const preferred = [...new Set([props.modelValue, browserTimeZone()].filter(Boolean))];
  return [...preferred, ...ALL_ZONES.filter((z) => !preferred.includes(z))];
});

const matches = computed(() => {
  const terms = query.value
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, ' ')
    .split(' ')
    .filter(Boolean);
  if (terms.length === 0) return zones.value.slice(0, MAX_MATCHES);
  return zones.value
    .filter((zone) => {
      const name = zone.toLowerCase().replace(/_/g, ' ');
      return terms.every((t) => name.includes(t));
    })
    .slice(0, MAX_MATCHES);
});

const offsets = new Map<string, string>();
function offsetLabel(zone: string): string {
  let label = offsets.get(zone);
  if (label === undefined) {
    try {
      label =
        new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
          .formatToParts(new Date())
          .find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      label = '';
    }
    offsets.set(zone, label);
  }
  return label;
}

function onQuery(event: Event) {
  query.value = (event.target as HTMLInputElement).value;
}

function onSelect(zone: unknown) {
  if (typeof zone === 'string' && zone) emit('update:modelValue', zone);
  query.value = '';
}
</script>
